/**
 * BSGOTECH Intelligent Technology Quiz — interactive engagement widget shown
 * on the landing page between "Ecosystem Architecture" and "Divisions".
 *
 * 8 questions, one at a time, animated progress. Result reveals a persona
 * (Explorer / Innovator / Visionary) with a call-to-action to explore the
 * four divisions.
 *
 * Multilingual DE / EN / PT via LangCtx.
 */
import React from "react";
import { CheckCircle2, RotateCcw, Sparkles, Brain, ArrowRight } from "lucide-react";
import { LangCtx } from "@/i18n/neunexus";

const P = {
  bg2:     "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  green:   "#22C55E",
  amber:   "#F59E0B",
  purple:  "#A855F7",
  rose:    "#F43F5E",
};

const DATA = {
  pt: {
    eyebrow: "Quiz Interativo",
    title: "Descobre o Futuro dos Ecossistemas Inteligentes.",
    subtitle: "Um quiz curto para perceberes como IA, Energia, Indústria e Saúde se cruzam no ecossistema BSGOTECH.",
    start: "Começar quiz",
    next: "Próxima",
    restart: "Refazer",
    q: "Pergunta",
    of: "de",
    exploreDivisions: "Explorar Divisões",
    scoreLabel: "Pontuação",
    personas: [
      { min: 0, max: 3, title: "Explorador Digital",              desc: "Estás a começar a conhecer o potencial das tecnologias inteligentes." },
      { min: 4, max: 6, title: "Inovador Tecnológico",            desc: "Entendes como IA, energia e indústria estão conectadas." },
      { min: 7, max: 8, title: "Visionário do Ecossistema Inteligente", desc: "Compreendes a transformação criada pela integração entre tecnologia, dados e automação." },
    ],
    tagline: "Conectamos IA + Energia + Indústria + Saúde para construir soluções inteligentes para o futuro.",
    questions: [
      { q: "O que define uma empresa tecnológica do futuro?", a: ["Apenas vender equipamentos", "Integrar dados, IA e sistemas conectados", "Trabalhar apenas com software", "Produzir apenas hardware"], correct: 1 },
      { q: "Qual é o papel da Inteligência Artificial nas empresas modernas?", a: ["Substituir todos os trabalhadores", "Analisar dados e apoiar decisões inteligentes", "Apenas criar imagens", "Guardar documentos"], correct: 1 },
      { q: "O que é uma Smart Factory?", a: ["Uma fábrica tradicional maior", "Uma fábrica conectada com automação, sensores, dados e IA", "Uma fábrica sem tecnologia", "Um armazém digital"], correct: 1 },
      { q: "Porque é importante o armazenamento de energia?", a: ["Porque permite maior estabilidade e utilização eficiente de energia renovável", "Porque elimina todas as redes elétricas", "Porque substitui todos os equipamentos", "Porque não precisa de gestão"], correct: 0 },
      { q: "O que é um Digital Twin?", a: ["Um segundo computador pessoal", "Uma representação digital de um equipamento ou processo real", "Uma fotografia de uma máquina", "Um programa de escritório"], correct: 1 },
      { q: "O que significa Industrial IoT?", a: ["Equipamentos industriais conectados que comunicam dados em tempo real", "Apenas internet para funcionários", "Redes sociais industriais", "Sistemas sem sensores"], correct: 0 },
      { q: "Como a IA pode ajudar na energia?", a: ["A prever consumo, otimizando produção e eficiência", "A criar eletricidade do nada", "A eliminar baterias", "A substituir painéis solares"], correct: 0 },
      { q: "Como diferentes setores podem trabalhar juntos através de uma plataforma tecnológica?", a: ["Usando uma infraestrutura comum de dados e inteligência", "Mantendo todos os sistemas isolados", "Eliminando comunicação entre equipamentos", "Evitando automação"], correct: 0 },
    ],
  },
  en: {
    eyebrow: "Interactive Quiz",
    title: "Discover the future of intelligent ecosystems.",
    subtitle: "A short quiz to see how AI, Energy, Industry and Health converge inside the BSGOTECH ecosystem.",
    start: "Start quiz",
    next: "Next",
    restart: "Restart",
    q: "Question",
    of: "of",
    exploreDivisions: "Explore Divisions",
    scoreLabel: "Score",
    personas: [
      { min: 0, max: 3, title: "Digital Explorer",              desc: "You're starting to see the potential of intelligent technologies." },
      { min: 4, max: 6, title: "Technology Innovator",          desc: "You understand how AI, energy and industry connect." },
      { min: 7, max: 8, title: "Intelligent Ecosystem Visionary", desc: "You grasp the transformation created by converging technology, data and automation." },
    ],
    tagline: "We connect AI + Energy + Industry + Health to build intelligent solutions for the future.",
    questions: [
      { q: "What defines a future technology company?", a: ["Only selling equipment", "Integrating data, AI and connected systems", "Only working with software", "Only producing hardware"], correct: 1 },
      { q: "What is the role of AI in modern companies?", a: ["Replacing every worker", "Analysing data and supporting intelligent decisions", "Only generating images", "Storing documents"], correct: 1 },
      { q: "What is a Smart Factory?", a: ["A larger traditional factory", "A connected factory with automation, sensors, data and AI", "A factory without technology", "A digital warehouse"], correct: 1 },
      { q: "Why is energy storage important?", a: ["Because it enables stability and efficient use of renewable energy", "Because it eliminates all power grids", "Because it replaces all equipment", "Because it needs no management"], correct: 0 },
      { q: "What is a Digital Twin?", a: ["A second personal computer", "A digital representation of a real asset or process", "A photograph of a machine", "An office program"], correct: 1 },
      { q: "What is Industrial IoT?", a: ["Connected industrial assets that stream data in real time", "Just internet for employees", "Industrial social networks", "Systems without sensors"], correct: 0 },
      { q: "How can AI help energy?", a: ["By forecasting consumption and optimising production and efficiency", "By creating electricity from nothing", "By eliminating batteries", "By replacing solar panels"], correct: 0 },
      { q: "How can different sectors work together through a technology platform?", a: ["Through a shared data and intelligence infrastructure", "By keeping every system isolated", "By eliminating communication between assets", "By avoiding automation"], correct: 0 },
    ],
  },
  de: {
    eyebrow: "Interaktives Quiz",
    title: "Entdecke die Zukunft intelligenter Ökosysteme.",
    subtitle: "Ein kurzes Quiz, um zu sehen, wie KI, Energie, Industrie und Gesundheit im BSGOTECH-Ökosystem zusammenwirken.",
    start: "Quiz starten",
    next: "Weiter",
    restart: "Neu starten",
    q: "Frage",
    of: "von",
    exploreDivisions: "Divisionen entdecken",
    scoreLabel: "Punkte",
    personas: [
      { min: 0, max: 3, title: "Digitaler Entdecker",                 desc: "Du beginnst, das Potenzial intelligenter Technologien zu erkennen." },
      { min: 4, max: 6, title: "Technologie-Innovator",               desc: "Du verstehst, wie KI, Energie und Industrie zusammenhängen." },
      { min: 7, max: 8, title: "Visionär intelligenter Ökosysteme",   desc: "Du erfasst die Transformation durch die Integration von Technologie, Daten und Automatisierung." },
    ],
    tagline: "Wir verbinden KI + Energie + Industrie + Gesundheit zu intelligenten Lösungen der Zukunft.",
    questions: [
      { q: "Was zeichnet ein Technologieunternehmen der Zukunft aus?", a: ["Nur Geräte verkaufen", "Daten, KI und vernetzte Systeme integrieren", "Nur mit Software arbeiten", "Nur Hardware produzieren"], correct: 1 },
      { q: "Welche Rolle spielt KI in modernen Unternehmen?", a: ["Alle Mitarbeiter ersetzen", "Daten analysieren und intelligente Entscheidungen unterstützen", "Nur Bilder erstellen", "Dokumente ablegen"], correct: 1 },
      { q: "Was ist eine Smart Factory?", a: ["Eine größere traditionelle Fabrik", "Eine vernetzte Fabrik mit Automatisierung, Sensoren, Daten und KI", "Eine Fabrik ohne Technologie", "Ein digitales Lager"], correct: 1 },
      { q: "Warum ist Energiespeicherung wichtig?", a: ["Sie ermöglicht Stabilität und effiziente Nutzung erneuerbarer Energie", "Sie ersetzt alle Stromnetze", "Sie ersetzt alle Geräte", "Sie braucht kein Management"], correct: 0 },
      { q: "Was ist ein Digital Twin?", a: ["Ein zweiter PC", "Eine digitale Repräsentation einer realen Anlage oder eines Prozesses", "Ein Foto einer Maschine", "Ein Office-Programm"], correct: 1 },
      { q: "Was bedeutet Industrial IoT?", a: ["Vernetzte Industrieanlagen, die Daten in Echtzeit senden", "Nur Internet für Mitarbeiter", "Industrielle soziale Netzwerke", "Systeme ohne Sensoren"], correct: 0 },
      { q: "Wie kann KI in der Energie helfen?", a: ["Verbrauch vorhersagen, Produktion und Effizienz optimieren", "Strom aus dem Nichts erzeugen", "Batterien überflüssig machen", "Solarmodule ersetzen"], correct: 0 },
      { q: "Wie können verschiedene Sektoren über eine Technologieplattform zusammenarbeiten?", a: ["Über eine gemeinsame Daten- und Intelligenz-Infrastruktur", "Indem alle Systeme isoliert bleiben", "Indem Kommunikation zwischen Anlagen entfällt", "Indem Automatisierung vermieden wird"], correct: 0 },
    ],
  },
};

const useT = () => {
  const { lang } = React.useContext(LangCtx);
  return DATA[lang] || DATA.en;
};

export default function TechQuiz() {
  const t = useT();
  const [started, setStarted] = React.useState(false);
  const [idx, setIdx] = React.useState(0);
  const [answers, setAnswers] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const total = t.questions.length;
  const finished = started && idx >= total;

  const score = answers.filter((v, i) => v === t.questions[i]?.correct).length;
  const persona = t.personas.find((p) => score >= p.min && score <= p.max) || t.personas[0];

  const pickAnswer = (i) => setSelected(i);
  const goNext = () => {
    if (selected === null) return;
    setAnswers([...answers, selected]);
    setSelected(null);
    setIdx(idx + 1);
  };
  const restart = () => { setStarted(false); setIdx(0); setAnswers([]); setSelected(null); };
  const cur = t.questions[idx];

  return (
    <section id="quiz" className="relative border-t" style={{ background: P.bg2, borderColor: P.border }} data-testid="tech-quiz">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.28em] font-semibold" style={{ background: `${P.purple}12`, color: P.purple, border: `1px solid ${P.purple}44` }}>
            <Brain size={11} /> {t.eyebrow}
          </div>
          <h2 className="mt-4 font-semibold tracking-tight text-[26px] lg:text-[36px] leading-[1.1]" style={{ color: "#fff", letterSpacing: "-0.015em" }}>
            {t.title}
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-[13.5px] leading-relaxed" style={{ color: P.dim }}>{t.subtitle}</p>
        </div>

        <div className="mt-10 rounded-xl p-6 lg:p-8"
          style={{ background: "rgba(8,11,18,0.6)", border: `1px solid ${P.border}` }}>

          {!started && (
            <div className="text-center py-6" data-testid="quiz-intro">
              <button onClick={() => setStarted(true)} data-testid="quiz-start"
                className="inline-flex items-center gap-2 px-6 py-3 text-[13px] font-semibold rounded-md transition"
                style={{ background: "#fff", color: "#0A0F1A" }}>
                <Sparkles size={14} /> {t.start}
              </button>
            </div>
          )}

          {started && !finished && cur && (
            <div data-testid="quiz-question">
              {/* Progress */}
              <div className="flex items-center justify-between mb-6">
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>
                  {t.q} {idx + 1} {t.of} {total}
                </div>
                <div className="flex gap-1.5">
                  {t.questions.map((_, i) => (
                    <span key={i} className="w-6 h-1 rounded-full transition"
                      style={{ background: i < idx ? P.green : i === idx ? P.purple : "rgba(255,255,255,0.1)" }} />
                  ))}
                </div>
              </div>

              <h3 className="text-[16px] lg:text-[19px] font-semibold leading-snug" style={{ color: "#fff" }}>{cur.q}</h3>

              <div className="mt-5 space-y-2">
                {cur.a.map((option, i) => {
                  const isSel = selected === i;
                  return (
                    <button key={i} onClick={() => pickAnswer(i)}
                      data-testid={`quiz-option-${i}`}
                      className="w-full text-left px-4 py-3 rounded-md text-[13px] transition"
                      style={{
                        background: isSel ? `${P.purple}14` : "rgba(255,255,255,0.02)",
                        border: `1px solid ${isSel ? P.purple : P.border}`,
                        color: isSel ? "#fff" : P.ink,
                      }}>
                      <span className="mr-2 font-mono text-[11px]" style={{ color: isSel ? P.purple : P.silver }}>{String.fromCharCode(65 + i)}.</span>
                      {option}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-end">
                <button onClick={goNext} disabled={selected === null} data-testid="quiz-next"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md transition"
                  style={{
                    background: selected === null ? "rgba(255,255,255,0.06)" : "#fff",
                    color: selected === null ? P.silver : "#0A0F1A",
                    cursor: selected === null ? "not-allowed" : "pointer",
                  }}>
                  {t.next} <ArrowRight size={13} />
                </button>
              </div>
            </div>
          )}

          {finished && (
            <div className="text-center py-4" data-testid="quiz-result">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center"
                style={{ background: `${P.purple}22`, border: `1px solid ${P.purple}66`, boxShadow: `0 0 40px ${P.purple}55` }}>
                <CheckCircle2 size={26} style={{ color: P.purple }} strokeWidth={1.75} />
              </div>
              <div className="mt-4 text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.purple }}>
                {t.scoreLabel} · {score} / {total}
              </div>
              <div className="mt-2 text-[24px] lg:text-[30px] font-semibold tracking-tight" style={{ color: "#fff", letterSpacing: "-0.01em" }}>
                {persona.title}
              </div>
              <p className="mt-3 max-w-lg mx-auto text-[13px] leading-relaxed" style={{ color: P.dim }}>{persona.desc}</p>

              <div className="mt-6 max-w-lg mx-auto rounded-md p-4"
                style={{ background: `${P.green}0d`, border: `1px solid ${P.green}33` }}>
                <div className="text-[10.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.green }}>BSGOTECH · Engineering Intelligent Ecosystems</div>
                <div className="mt-2 text-[13px] leading-relaxed" style={{ color: P.ink }}>{t.tagline}</div>
              </div>

              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <a href="#divisions" data-testid="quiz-explore"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md" style={{ background: "#fff", color: "#0A0F1A" }}>
                  {t.exploreDivisions} <ArrowRight size={13} />
                </a>
                <button onClick={restart} data-testid="quiz-restart"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md transition hover:bg-white/5"
                  style={{ border: `1px solid ${P.border}`, color: P.ink }}>
                  <RotateCcw size={12} /> {t.restart}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
